import { useState, createContext, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Camera, Building2, X, Sparkles } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { getLangFromPath, localizePath } from '../i18n';

const REGISTRO_FOTOGRAFO_URL = 'https://moment-ia.shop/registro-fotografo';

const EmpezarCtx = createContext({ open: () => {}, close: () => {} });

export const useEmpezar = () => useContext(EmpezarCtx);

export function EmpezarProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const value = { open: () => setIsOpen(true), close: () => setIsOpen(false) };

  const goToDemo = (e) => {
    e.preventDefault();
    const lang = getLangFromPath(location.pathname);
    setIsOpen(false);
    navigate(localizePath('/request-demo', lang));
  };

  return (
    <EmpezarCtx.Provider value={value}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="max-w-2xl border-white/10 bg-[#0f0f14] p-0 overflow-hidden"
          data-testid="empezar-modal"
        >
          <div className="relative p-8 md:p-10">
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-colors"
              aria-label={t('empezarModal.close')}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-violet-400/25 bg-violet-500/10 text-xs text-violet-200 mb-4">
                <Sparkles className="w-3 h-3" /> {t('empezarModal.badge')}
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
                {t('empezarModal.title')}
              </h2>
              <p className="mt-2 text-sm text-white/60 max-w-md mx-auto">
                {t('empezarModal.subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <a
                href={REGISTRO_FOTOGRAFO_URL}
                onClick={() => setIsOpen(false)}
                data-testid="empezar-fotografo"
                className="group relative overflow-hidden rounded-2xl border border-violet-400/25 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-transparent p-6 hover:border-violet-400/50 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-violet-500/20 border border-violet-400/30 flex items-center justify-center text-violet-200 mb-4">
                  <Camera className="w-5 h-5" />
                </div>
                <div className="font-display text-lg font-semibold text-white mb-1">
                  {t('empezarModal.photographerTitle')}
                </div>
                <p className="text-sm text-white/60 leading-relaxed mb-6">
                  {t('empezarModal.photographerDesc')}
                </p>
                <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-200 group-hover:gap-3 transition-all">
                  {t('empezarModal.photographerCta')} <ArrowRight className="w-4 h-4" />
                </div>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-violet-500/20 rounded-full blur-2xl group-hover:bg-violet-500/30 transition-colors" />
              </a>

              <a
                href="#"
                onClick={goToDemo}
                data-testid="empezar-empresa"
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 via-white/[0.02] to-transparent p-6 hover:border-white/25 transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center text-white mb-4">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="font-display text-lg font-semibold text-white mb-1">
                  {t('empezarModal.companyTitle')}
                </div>
                <p className="text-sm text-white/60 leading-relaxed mb-6">
                  {t('empezarModal.companyDesc')}
                </p>
                <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-white group-hover:gap-3 transition-all">
                  {t('empezarModal.companyCta')} <ArrowRight className="w-4 h-4" />
                </div>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-colors" />
              </a>
            </div>

            <div className="mt-6 text-center text-xs text-white/40">
              {t('empezarModal.haveAccount')}{' '}
              <a href="https://moment-ia.shop/login" className="text-violet-300 hover:text-violet-200 font-medium">
                {t('empezarModal.login')}
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </EmpezarCtx.Provider>
  );
}
